import React from 'react';
import { FormField, FormSelect, FormSection } from '../../../shared/FormComponents';
import { FREQUENCY_OPTIONS, TOOLTIPS, FILING_STATUS_OPTIONS } from '../../../shared/constants';
import { formatCurrency } from '../../../../utils/incomeUtils';

const PaymentDetailsSection = ({ register, errors, watch, setValue }) => {
  const grossPay = watch('grossPay');
  const frequency = watch('frequency');
  const taxSummary = watch('taxSummary') || {};
  
  const handleGrossPayChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and one decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      return;
    }
    
    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      return;
    }
    
    setValue('grossPay', cleanValue, { shouldValidate: true });
  };

  const handleGrossPayBlur = (e) => {
    const value = e.target.value;
    if (!value) {
      setValue('grossPay', '', { shouldValidate: true });
      return;
    }

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setValue('grossPay', numValue.toFixed(2), { shouldValidate: true });
    }
  };

  // Calculate estimated annual gross pay based on frequency
  const calculateEstimatedAnnualGrossPay = () => {
    if (!grossPay || !frequency) return 0;
    
    const numGrossPay = parseFloat(grossPay);
    if (isNaN(numGrossPay)) return 0;
    
    let annualPay = 0;
    
    switch (frequency.toLowerCase()) {
      case 'weekly':
        annualPay = numGrossPay * 52;
        break;
      case 'biweekly':
        annualPay = numGrossPay * 26;
        break;
      case 'monthly':
        annualPay = numGrossPay * 12;
        break;
      case 'quarterly':
        annualPay = numGrossPay * 4;
        break;
      case 'yearly':
        annualPay = numGrossPay;
        break;
      default:
        annualPay = numGrossPay * 12;
    }
    
    console.log('📊 PaymentDetails - Annual gross pay calculation:', { 
      grossPay: numGrossPay, 
      frequency, 
      annualPay 
    });
    
    return annualPay;
  };

  // Calculate estimated net pay (per frequency period)
  const calculateEstimatedNetPay = () => {
    if (!grossPay || !frequency || !taxSummary.totalTax) return 0;
    
    const numGrossPay = parseFloat(grossPay);
    if (isNaN(numGrossPay)) return 0;
    
    // Convert annual tax to frequency-based tax
    const annualTax = taxSummary.totalTax || 0;
    let frequencyTax = 0;
    
    switch (frequency.toLowerCase()) {
      case 'weekly':
        frequencyTax = annualTax / 52;
        break;
      case 'biweekly':
        frequencyTax = annualTax / 26;
        break;
      case 'monthly':
        frequencyTax = annualTax / 12;
        break;
      case 'quarterly':
        frequencyTax = annualTax / 4;
        break;
      case 'yearly':
        frequencyTax = annualTax;
        break;
      default:
        frequencyTax = annualTax / 12;
    }
    
    const netPay = Math.max(0, numGrossPay - frequencyTax);
    
    console.log('💰 PaymentDetails - Net pay calculation:', { 
      grossPay: numGrossPay, 
      frequency, 
      annualTax, 
      frequencyTax, 
      netPay 
    });
    
    return netPay;
  };

  // Calculate estimated net annual pay
  const calculateEstimatedNetAnnualPay = () => {
    if (!grossPay || !frequency || !taxSummary.totalTax) return 0;
    
    const annualGross = calculateEstimatedAnnualGrossPay();
    const annualTax = taxSummary.totalTax || 0;
    const netAnnualPay = Math.max(0, annualGross - annualTax);
    
    console.log('💰 PaymentDetails - Net annual pay calculation:', { 
      annualGross, 
      annualTax, 
      netAnnualPay 
    });
    
    return netAnnualPay;
  };

  return (
    <FormSection title="Payment Details">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          label="Gross Pay Check *"
          name="grossPay"
          type="text"
          inputMode="decimal"
          prefix="$"
          placeholder="0.00"
          register={register}
          error={errors.grossPay}
          tooltip={TOOLTIPS.grossPay}
          onChange={handleGrossPayChange}
          onBlur={handleGrossPayBlur}
        />

        <FormSelect
          label="Pay Frequency *"
          name="frequency"
          options={FREQUENCY_OPTIONS}
          register={register}
          error={errors.frequency}
          tooltip={TOOLTIPS.frequency}
        />

        <FormField
          label="Estimated Annual Gross Pay"
          name="estimatedAnnualGrossPay"
          type="text"
          prefix="$"
          placeholder="Select frequency and pay amount"
          value={grossPay && frequency ? formatCurrency(calculateEstimatedAnnualGrossPay()) : ''}
          register={register}
          readOnly
          tooltip="Estimated annual gross pay based on your pay frequency and contract period"
        />

        <FormField
          label="Estimated Net Pay"
          name="estimatedNetPay"
          type="text"
          prefix="$"
          placeholder="Select frequency and pay amount"
          value={grossPay && frequency ? formatCurrency(calculateEstimatedNetPay()) : ''}
          register={register}
          readOnly
          tooltip={`Estimated net pay per ${frequency ? frequency.toLowerCase() : 'pay period'} after taxes`}
        />

        <FormField
          label="Estimated Net Annual Pay"
          name="estimatedNetAnnualPay"
          type="text"
          prefix="$"
          placeholder="Select frequency and pay amount"
          value={grossPay && frequency ? formatCurrency(calculateEstimatedNetAnnualPay()) : ''}
          register={register}
          readOnly
          tooltip="Estimated net annual pay after taxes"
        />

        <FormSelect
          label="Filing Status *"
          name="filingStatus"
          options={FILING_STATUS_OPTIONS}
          register={register}
          error={errors.filingStatus}
          tooltip={TOOLTIPS.filingStatus}
        />

        <FormSelect
          label="Business Structure *"
          name="businessStructure"
          register={register}
          error={errors.businessStructure}
          tooltip="Select your business structure type"
          options={[
            { id: 'sole_proprietor', label: 'Sole Proprietor' },
            { id: 'llc', label: 'LLC' },
            { id: 's_corp', label: 'S-Corp' },
            { id: 'c_corp', label: 'C-Corp' }
          ]}
        />
      </div>
    </FormSection>
  );
};

export default PaymentDetailsSection;